import { Trophy, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RulesBlock, hasRules } from "@/components/ContestRules";
import { OtpVerification } from "@/components/verify/OtpVerification";
import api from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" });

const Info = ({ label, value, mono }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-semibold ${mono ? "font-mono-code" : ""}`}>{value}</div>
  </div>
);

function StatusBadge({ receipt }) {
  if (receipt.redeemed) return <Badge data-testid="verify-status-redeemed" className="bg-slate-500/15 text-slate-300 border border-slate-500/30">GIÀ RITIRATO</Badge>;
  if (receipt.expired) return <Badge data-testid="verify-status-expired" className="bg-destructive/15 text-destructive border border-destructive/30">SCADUTA</Badge>;
  return <Badge data-testid="verify-status-valid" className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">VALIDA</Badge>;
}

export function ReceiptResult({ receipt, store, busy, run, onRedeemed, onReset, autoOtp = false }) {
  const reprint = async () => {
    const r = await run(() => api.reprintReceipt(receipt.receipt_serial), "Ristampa non riuscita");
    if (!r) return;
    if (r.mode === "browser") window.open(api.receiptPdfUrl(receipt.id, receipt.receipt_token), "_blank", "noopener");
    toast.success(r.message);
  };
  return (
    <Card className="p-6 bg-card border-border space-y-4" data-testid="verify-result">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-amber-500/15 grid place-items-center"><Trophy className="h-6 w-6 text-amber-400" /></div>
          <div>
            <h2 className="font-display font-bold text-xl">{receipt.prize_name}</h2>
            <p className="text-sm text-muted-foreground">{receipt.prize_type || "Premio"} {receipt.prize_value ? `· ${receipt.prize_value}` : ""}</p>
          </div>
        </div>
        <StatusBadge receipt={receipt} />
      </div>

      <div className="text-sm grid grid-cols-2 gap-y-2 gap-x-4 rounded-lg bg-secondary/40 p-4">
        <Info label="Seriale" value={receipt.receipt_serial} mono />
        <Info label="Concorso" value={receipt.contest_name} />
        <Info label="Data vincita" value={fmt(receipt.created_at)} />
        <Info label="Codice giocata" value={receipt.code} mono />
        {receipt.store_name && <Info label="Codice emesso da" value={receipt.store_name} />}
        {receipt.expires_at && <Info label="Valida fino al" value={fmt(receipt.expires_at)} />}
        {receipt.redeemed && (
          <>
            <Info label="Ritirato il" value={fmt(receipt.redeemed_at)} />
            <Info label="Sede ritiro" value={receipt.redeemed_store || "—"} />
            <Info label="Verifica OTP" value={receipt.redeemed_otp_verified ? "Superata" : "—"} />
          </>
        )}
      </div>

      {hasRules(receipt.contest_rules) && (
        <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-2" data-testid="verify-rules-box">
          <p className="text-sm font-semibold">Regolamento e contatti del concorso</p>
          <RulesBlock c={{ ...receipt.contest_rules, name: receipt.contest_name, id: receipt.contest_id }} source="punto_vendita" />
        </div>
      )}

      {!receipt.redeemed && !receipt.expired && (
        <OtpVerification receipt={receipt} store={store} busy={busy} run={run} onRedeemed={onRedeemed} resetKey={receipt.receipt_serial} autoOtp={autoOtp} />
      )}

      <div className="flex flex-wrap gap-2">
        <Button data-testid="reprint-receipt-button" variant="outline" className="h-12 gap-2 border-border" disabled={busy} onClick={reprint}>
          <RotateCcw className="h-4 w-4" /> Ristampa scontrino
        </Button>
        {receipt.redeemed && (
          <a href={api.pickupReceiptPdfUrl(receipt.id)} target="_blank" rel="noopener noreferrer" data-testid="pickup-receipt-button" className="flex-1">
            <Button className="w-full gap-2 h-12"><Printer className="h-4 w-4" /> Stampa ricevuta di ritiro</Button>
          </a>
        )}
        <Button data-testid="verify-reset-button" variant="secondary" onClick={onReset} className="h-12">Nuova verifica</Button>
      </div>
    </Card>
  );
}
